import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Home, Package, Clock3, Shield, Calendar, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import MobileTopBar from "../components/MobileTopBar";
import StudioFooter from "../components/StudioFooter";
import PrintingCalculator from "../components/PrintingCalculator";
import UnitRateCard from "../components/UnitRateCard";
import { findSub } from "../lib/services";
import { useOverrides, applySubOverride } from "../context/OverridesContext";
import { useStudio } from "../context/StudioContext";
import { usePlanDrawer } from "../context/PlanDrawerContext";

export default function SubServicePage() {
  const { serviceSlug, subSlug } = useParams();
  const navigate = useNavigate();
  const { addToPlan, isInPlan } = useStudio();
  const { openDrawer } = usePlanDrawer();
  const { overrides } = useOverrides();
  const { service, sub: rawSub } = findSub(serviceSlug, subSlug);
  const sub = rawSub ? applySubOverride(rawSub, overrides) : null;

  const hasPlans = sub && Array.isArray(sub.plans) && sub.plans.length > 0;
  const defaultPlanKey = hasPlans
    ? (sub.plans.find((p) => p.popular) || sub.plans[0]).key
    : null;
  const [selectedPlan, setSelectedPlan] = useState(defaultPlanKey);

  const { prev, next } = useMemo(() => {
    if (!service) return { prev: null, next: null };
    const idx = service.subs.findIndex((x) => x.slug === subSlug);
    return {
      prev: idx > 0 ? service.subs[idx - 1] : null,
      next: idx < service.subs.length - 1 ? service.subs[idx + 1] : null,
    };
  }, [service, subSlug]);

  if (!service || !sub) {
    return (
      <div className="flex min-h-screen bg-brand-ink">
        <Sidebar />
        <main className="flex-1 p-10">
          <div className="font-serif-display text-3xl">Sub-service not found</div>
          <button onClick={() => navigate("/dashboard")} className="mt-4 text-brand">
            Back to Dashboard
          </button>
        </main>
      </div>
    );
  }

  const activePlan = hasPlans ? sub.plans.find((p) => p.key === selectedPlan) : null;

  const addSelectedPlan = (planOverride) => {
    const p = planOverride || activePlan;
    if (hasPlans && p) {
      const key = `${sub.code}:${p.key}`;
      if (isInPlan(key)) {
        toast(`${sub.title} (${p.name}) is already in your plan.`);
      } else {
        addToPlan({
          key,
          code: sub.code,
          title: sub.title,
          planName: p.name,
          service: service.label,
          price: p.price,
          billing: sub.billing,
          serviceSlug: service.slug,
          subSlug: sub.slug,
        });
        toast.success(`${sub.title} (${p.name}) added to your plan.`);
      }
    } else {
      const key = sub.code;
      if (isInPlan(key)) {
        toast(`${sub.title} is already in your plan.`);
      } else {
        addToPlan({
          key,
          code: sub.code,
          title: sub.title,
          service: service.label,
          price: sub.price,
          billing: sub.billing,
          serviceSlug: service.slug,
          subSlug: sub.slug,
        });
        toast.success(`${sub.title} added to your plan.`);
      }
    }
    openDrawer();
  };

  const gridCols =
    sub.planColumns === 1
      ? "md:grid-cols-1"
      : sub.planColumns === 3
      ? "lg:grid-cols-3"
      : "md:grid-cols-2";

  return (
    <div className="flex min-h-screen bg-brand-ink">
      <Sidebar />
      <main className="flex-1 overflow-x-hidden">
        <MobileTopBar title="Studio" />
        <div className="mx-auto max-w-[1240px] px-5 py-8 sm:px-8 sm:py-10">
          {/* Breadcrumbs */}
          <div className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
            <Link to="/dashboard" className="inline-flex items-center gap-2 hover:text-brand">
              <Home className="h-3 w-3" /> Dashboard
            </Link>
            <span className="text-brand-mute/40">/</span>
            <Link to={`/service/${service.slug}`} className="hover:text-brand">
              {service.label}
            </Link>
            <span className="text-brand-mute/40">/</span>
            <span className="text-brand">{sub.title}</span>
          </div>

          {/* Header */}
          <div className="mt-6 overflow-hidden rounded-md border border-brand-hair bg-brand-panel">
            {sub.image && sub.cardType !== "logos" && (
              <div className="relative h-52 w-full overflow-hidden bg-brand-panel2">
                <img
                  src={sub.image}
                  alt={sub.title}
                  className="h-full w-full object-cover opacity-70"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-panel via-brand-panel/40 to-transparent" />
                <div className="absolute left-6 top-6 rounded-sm bg-black/60 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.22em] text-brand-ivory backdrop-blur-sm">
                  {sub.code}
                </div>
              </div>
            )}
            {sub.cardType === "logos" && sub.logos && (
              <div className="flex h-40 w-full items-center justify-center gap-6 bg-white px-6">
                {sub.logos.map((l, i) => (
                  <img key={i} src={l.src} alt={l.alt || ""} className="max-h-14 max-w-[40%] object-contain" />
                ))}
              </div>
            )}
            <div className="p-5 sm:p-8">
            <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
              <span>{sub.code}</span>
              <span className="text-brand-mute/40">—</span>
              <span>{service.label}</span>
            </div>
            <h1 className="mt-3 font-serif-display text-[52px] font-medium leading-[1.02] tracking-tight">
              {sub.title}
            </h1>
            <p className="mt-4 max-w-[720px] text-[15px] leading-relaxed text-brand-ivory/70">
              {sub.description}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3 font-mono text-[10.5px] uppercase tracking-[0.28em]">
              <span className="rounded-md bg-brand/15 px-3 py-1.5 text-brand">{sub.billing}</span>
              {sub.timeline && (
                <span className="inline-flex items-center gap-2 rounded-md border border-brand-hair px-3 py-1.5 text-brand-ivory/70">
                  <Calendar className="h-3.5 w-3.5" /> {sub.timeline}
                </span>
              )}
              {sub.deliverables && (
                <span className="inline-flex items-center gap-2 rounded-md border border-brand-hair px-3 py-1.5 text-brand-ivory/70">
                  <Package className="h-3.5 w-3.5" /> {sub.deliverables} deliverables
                </span>
              )}
              {hasPlans && (
                <span className="inline-flex items-center gap-2 rounded-md border border-brand-hair px-3 py-1.5 text-brand-ivory/70">
                  <Sparkles className="h-3.5 w-3.5" /> {sub.plans.length} {sub.plans.length === 1 ? "PLAN" : "PLANS"}
                </span>
              )}
            </div>

            {sub.externalLink && (
              <a
                href={sub.externalLink.href}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 rounded-md border border-brand-hair px-4 py-2.5 font-mono text-[10.5px] uppercase tracking-[0.28em] text-brand-ivory/85 transition-colors hover:border-brand hover:text-brand"
              >
                <ExternalLink className="h-3.5 w-3.5" /> {sub.externalLink.label}
              </a>
            )}
            </div>
          </div>

          {/* Real-time calculator (Printing) */}
          {sub.type === "calculator" && (
            <PrintingCalculator service={service} sub={sub} />
          )}

          {/* Unit-rate items (WhatsApp, Giveaway, Influencer per unit) */}
          {!sub.type && sub.unitRate && sub.allowsQuantity && (
            <UnitRateCard service={service} sub={sub} />
          )}

          {/* Plan tiers OR single price card */}
          {!sub.type && !(sub.unitRate && sub.allowsQuantity) && (hasPlans ? (
            <div className="mt-10">
              <div className="mb-4 flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                  {sub.plansHeading || "Choose a plan"}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                  {sub.plans.length} option{sub.plans.length !== 1 ? "s" : ""}
                </div>
              </div>
              <div className={`grid grid-cols-1 gap-5 ${gridCols}`}>
                {sub.plans.map((p) => {
                  const active = selectedPlan === p.key;
                  return (
                    <div
                      key={p.key}
                      className={`relative flex flex-col rounded-md border p-6 transition-colors ${
                        active
                          ? "border-brand bg-brand/[0.05]"
                          : "border-brand-hair bg-brand-panel hover:border-brand/40"
                      }`}
                    >
                      {p.popular && (
                        <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-1 font-mono text-[9.5px] uppercase tracking-[0.24em] text-white">
                          <span className="h-1.5 w-1.5 rounded-full bg-white" />
                          Most Popular
                        </div>
                      )}
                      <button
                        onClick={() => setSelectedPlan(p.key)}
                        className="absolute right-4 top-4 flex h-5 w-5 items-center justify-center rounded-full border border-brand-hair"
                        aria-label={`Select ${p.name}`}
                      >
                        {active && <span className="h-2.5 w-2.5 rounded-full bg-brand" />}
                      </button>
                      <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                        {p.name}
                      </div>
                      <div className="mt-2 font-serif-display text-[40px] leading-none text-brand-ivory">
                        {p.price}
                      </div>
                      <div className="mt-2 font-mono text-[10.5px] uppercase tracking-[0.22em] text-brand-mute">
                        {p.priceNote || sub.priceNote || sub.billing}
                      </div>

                      <ul className="mt-6 space-y-2.5">
                        {(p.features || []).map((f, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-[13px] text-brand-ivory/85">
                            <span className="mt-[5px] flex h-4 w-4 flex-none items-center justify-center rounded-full bg-brand/15 text-brand">
                              <Check className="h-3 w-3" />
                            </span>
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={() => {
                          setSelectedPlan(p.key);
                          addSelectedPlan(p);
                        }}
                        className={`group mt-6 flex items-center justify-center gap-2 rounded-md py-3 font-mono text-[11px] font-semibold uppercase tracking-[0.28em] transition-colors ${
                          active
                            ? "bg-brand text-white hover:bg-[#ff5c26]"
                            : "border border-brand-hair text-brand-ivory hover:border-brand hover:text-brand"
                        }`}
                      >
                        Add Plan
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-[1fr_360px]">
              <div className="rounded-md border border-brand-hair bg-brand-panel p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                  What's Included
                </div>
                <ul className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  {(sub.included || []).map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-[13.5px] text-brand-ivory/85">
                      <span className="mt-[6px] flex h-4 w-4 flex-none items-center justify-center rounded-full bg-brand/15 text-brand">
                        <Check className="h-3 w-3" />
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-md border border-brand-hair bg-brand-panel p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                  Price
                </div>
                <div className="mt-3 font-serif-display text-[42px] leading-none text-brand-ivory">
                  {sub.price}
                </div>
                <div className="mt-2 font-mono text-[10.5px] uppercase tracking-[0.22em] text-brand-mute">
                  {sub.priceNote || sub.billing}
                </div>
                <button
                  onClick={() => addSelectedPlan(null)}
                  className="group mt-6 flex w-full items-center justify-center gap-3 rounded-md bg-brand py-3.5 font-mono text-[12px] font-semibold uppercase tracking-[0.28em] text-white transition-colors hover:bg-[#ff5c26]"
                >
                  Add to Plan
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
                <div className="mt-4 space-y-2 text-[12px] text-brand-ivory/60">
                  <PriceRow icon={<Clock3 className="h-3.5 w-3.5" />} text="Response within 24 hours" />
                  <PriceRow icon={<Shield className="h-3.5 w-3.5" />} text="Fixed scope. No surprises." />
                  <PriceRow icon={<Check className="h-3.5 w-3.5" />} text="Only paid after scope approval" />
                </div>
              </div>
            </div>
          ))}

          {/* Included + How it works (for plan mode) */}
          {hasPlans && (
            <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-2">
              <div className="rounded-md border border-brand-hair bg-brand-panel p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                  Included in every plan
                </div>
                <ul className="mt-4 space-y-3">
                  {(sub.included || []).map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-[13.5px] text-brand-ivory/85">
                      <span className="mt-[6px] flex h-4 w-4 flex-none items-center justify-center rounded-full bg-brand/15 text-brand">
                        <Check className="h-3 w-3" />
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-md border border-brand-hair bg-brand-panel p-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                  How it works
                </div>
                <ol className="mt-4 space-y-4">
                  {[
                    { code: "01", title: "Pick a plan", copy: "Choose the tier that matches your reach & budget. Add it to your plan." },
                    { code: "02", title: "Checkout & brief", copy: "Share your project brief and confirm. We respond within 24 hours with scope + timeline." },
                    { code: "03", title: "Kick-off", copy: "We start immediately on approved scope. No calls unless you want one." },
                  ].map((step) => (
                    <li key={step.code} className="flex gap-4">
                      <span className="mt-0.5 font-mono text-[11px] tracking-[0.22em] text-brand">{step.code}</span>
                      <div>
                        <div className="text-[13.5px] font-medium text-brand-ivory">{step.title}</div>
                        <div className="mt-1 text-[12.5px] leading-relaxed text-brand-ivory/60">{step.copy}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          {/* More in category */}
          {service.subs.length > 1 && (
            <div className="mt-10">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                More in <span className="text-brand-ivory">{service.label}</span>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                {service.subs
                  .filter((s) => s.slug !== sub.slug)
                  .slice(0, 4)
                  .map((s) => (
                    <button
                      key={s.code}
                      onClick={() => navigate(`/service/${service.slug}/${s.slug}`)}
                      className="group flex items-center justify-between rounded-md border border-brand-hair bg-brand-panel px-5 py-4 text-left transition-colors hover:border-brand/60"
                    >
                      <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-mute">{s.code}</div>
                        <div className="mt-1 font-serif-display text-[18px] leading-tight tracking-tight text-brand-ivory">
                          {s.title}
                        </div>
                        <div className="text-[12.5px] text-brand-ivory/55">{s.subtitle}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="font-mono text-[12px] text-brand-ivory/80">{s.price}</div>
                        <ArrowRight className="h-4 w-4 text-brand-mute transition-colors group-hover:text-brand" />
                      </div>
                    </button>
                  ))}
              </div>
            </div>
          )}

          {/* Prev / Next */}
          <div className="mt-10 grid grid-cols-1 gap-3 md:grid-cols-2">
            <PrevNext label="Previous" title={prev?.title} onClick={prev ? () => navigate(`/service/${service.slug}/${prev.slug}`) : null} dir="left" />
            <PrevNext label="Next" title={next?.title} onClick={next ? () => navigate(`/service/${service.slug}/${next.slug}`) : null} dir="right" />
          </div>

          <StudioFooter code={sub.code} label={sub.title} />
        </div>
      </main>
    </div>
  );
}

function PriceRow({ icon, text }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-brand">{icon}</span>
      <span>{text}</span>
    </div>
  );
}

function PrevNext({ label, title, onClick, dir }) {
  const disabled = !onClick;
  return (
    <button
      onClick={onClick || (() => {})}
      disabled={disabled}
      className={`group flex items-center justify-between rounded-md border border-brand-hair bg-brand-panel px-5 py-4 text-left transition-colors ${
        disabled ? "opacity-40" : "hover:border-brand/60"
      } ${dir === "right" ? "md:flex-row" : "md:flex-row-reverse"}`}
    >
      <div className={dir === "right" ? "" : "text-right"}>
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">{label}</div>
        <div className="mt-1 font-serif-display text-[17px] tracking-tight text-brand-ivory">
          {title || "—"}
        </div>
      </div>
      {dir === "right" ? (
        <ArrowRight className="h-5 w-5 text-brand-mute transition-colors group-hover:text-brand" />
      ) : (
        <ArrowLeft className="h-5 w-5 text-brand-mute transition-colors group-hover:text-brand" />
      )}
    </button>
  );
}
